/**
 * Parse + format basket from CS-Cart REST cart_content response.
 *
 * Migration depuis apollo/server/resolvers/Basket/getBasket.ts vers cote front.
 * Centralise la logique de transformation produits/shippings/promotions/replace
 * pour reutilisation par useBasket (TanStack) sans passer par GraphQL resolver.
 */

import { formatProductCommonBasket } from '@/lib/formatProduct'

const parseNumberShipping = (value: any): number => {
  return Number(isNaN(value) ? 0 : value)
}

const parseProductBasket = (products: any) => {
  if (!products) return []
  return Object.keys(products).map(item_id => {
    const product: any = products[item_id]

    // storeBagProgramPharmacyBudgetId : ensure array
    const extra = product.extra
      ? {
          ...product.extra,
          storeBagProgramPharmacyBudgetId: product.extra.storeBagProgramPharmacyBudgetId
            ? Array.isArray(product.extra.storeBagProgramPharmacyBudgetId)
              ? product.extra.storeBagProgramPharmacyBudgetId
              : typeof product.extra.storeBagProgramPharmacyBudgetId === 'object'
                ? Object.values(product.extra.storeBagProgramPharmacyBudgetId)
                : [product.extra.storeBagProgramPharmacyBudgetId]
            : [],
        }
      : null

    // Replace pending : flags injectes par hook extract_cart CS-Cart
    const isReplaceLocked = !!product?.extra?._replace_locked
    const replaceSourceOrderId = product?.extra?._replace_source_order_id
      ? String(product.extra._replace_source_order_id)
      : null

    return {
      ...product,
      item_id,
      // Cast product_id en string : aligné avec SETTINGS.FRAIS_PREPARATION_*_ID (string)
      // et resolver GraphQL legacy (String type). Filtres frais de gestion fonctionnent.
      product_id: product.product_id != null ? String(product.product_id) : product.product_id,
      extra,
      // Traduit les champs bruts CS-Cart en champs attendus par le front :
      // qty_step -> coeffQuantity (pas du PCB), tracking -> infiniteStock, fallback seo_name.
      // Sans ca, ProductQtyInput retombe sur un pas de 1 et le "+" n'ajoute plus un PCB.
      ...formatProductCommonBasket(product),
      product_features: product?.product_features ? Object.values(product.product_features) : null,
      is_replace_locked: isReplaceLocked,
      replace_source_order_id: replaceSourceOrderId,
    }
  })
}

const parseProductGroups = (productGroups: any[] = []) => {
  return productGroups.map(pg => ({
    ...pg,
    products: parseProductBasket(pg.products),
    shippings: (pg.shippings ?? []).map((s: any) => {
      const rateValue = s?.rate_info?.rate_value?.C ? Object.values(s.rate_info.rate_value.C) : []

      return {
        ...s,
        rate_info: {
          base_rate: parseNumberShipping(s.rate_info?.base_rate),
          rate_value:
            rateValue.length > 0
              ? rateValue.map((rv: any) => ({
                  range_from_value: Number(rv.range_from_value),
                  range_to_value: Number(rv.range_to_value),
                  value: parseNumberShipping(rv.value),
                }))
              : [
                  {
                    range_from_value: 0,
                    range_to_value: 0,
                    value: parseNumberShipping(s.rate_info?.base_rate),
                  },
                ],
        },
      }
    }),
  }))
}

/**
 * Helper : retourne keys d'un hash obj OU array tel quel.
 * REST CartContentService renvoie soit hash obj soit array selon contexte.
 */
const toArrayKeys = (val: any): string[] => {
  if (Array.isArray(val)) return val
  if (val && typeof val === 'object') return Object.keys(val)
  return []
}

const toArrayValues = (val: any): any[] => {
  if (Array.isArray(val)) return val
  if (val && typeof val === 'object') return Object.values(val)
  return []
}

const toPaymentsArray = (val: any): any[] => {
  if (Array.isArray(val)) return val
  if (val && typeof val === 'object') {
    return Object.keys(val).map(id => ({ id, ...(val[id] ?? {}) }))
  }
  return []
}

/**
 * Transforme la response brute CartContentService::getCartContent en Basket
 * format consommable par les composants front.
 */
export const parseBasket = (raw: any) => {
  if (!raw) return null

  // Replace pending raw response can be wrapped { status, data } OR direct basket.
  const cart = raw?.data && typeof raw.data === 'object' ? raw.data : raw

  const replaceSumOld = cart._replace_sum_old != null ? Number(cart._replace_sum_old) : null
  const replaceOldOrderIds = Array.isArray(cart._replace_old_order_ids)
    ? cart._replace_old_order_ids.map((id: any) => String(id))
    : null
  const replaceGraceUntil = cart._replace_grace_until != null ? Number(cart._replace_grace_until) : null
  const replaceRequiredPaymentId = cart._replace_required_payment_id != null
    ? Number(cart._replace_required_payment_id)
    : null

  return {
    ...cart,
    totalQuantity: cart.amount,
    coupons: toArrayKeys(cart.coupons),
    product_groups: parseProductGroups(cart.product_groups ?? []),
    applied_promotions: toArrayValues(cart.applied_promotions),
    payments: toPaymentsArray(cart.payments),
    replace_sum_old: replaceSumOld,
    replace_old_order_ids: replaceOldOrderIds,
    replace_grace_until: replaceGraceUntil,
    replace_required_payment_id: replaceRequiredPaymentId,
  }
}
